import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { PageHeader, FilterBar, DataTable, Modal, Badge, EmptyState } from '../components/ui/SharedUI';
import RentScheduleGenerateForm from '../components/forms/RentScheduleGenerateForm';
import { demoRepository } from '../repository/demoRepository';
import { rentScheduleService } from '../services/rentScheduleService';
import { calculations } from '../utils/calculations';
import { useFeedback } from '../components/ui/FeedbackUI';
import { pocketbaseService } from '../services/pocketbaseService';
import SafeIcon from '../common/SafeIcon';

const getScheduleCalculationHelp = (row) => {
  const calc = row?.metadata?.rentCalculation;
  if (!calc) {
    return `Gross rent ${Number(row?.grossRentDue || 0).toFixed(2)} − total deductions ${Number(row?.totalDeductions || 0).toFixed(2)} = net owner ${Number(row?.netOwnerAmount || 0).toFixed(2)}. This older row does not contain the detailed rent-calculation snapshot.`;
  }
  return `Annual rent: $${Number(calc.rentAmount || 0).toFixed(2)} ${calc.rentBasis} × ${calc.rentBasisPeriodsPerYear} = $${Number(calc.annualRent || 0).toFixed(2)}. Gross per ${calc.paymentFrequency} payment: $${Number(calc.annualRent || 0).toFixed(2)} ÷ ${calc.paymentPeriodsPerYear} = $${Number(calc.grossRentPerPeriod || 0).toFixed(2)}. Agent fee: $${Number(calc.grossRentPerPeriod || 0).toFixed(2)} × ${Number(calc.agentFeePercent || 0).toFixed(2)}% = $${Number(calc.agentFeeAmount || 0).toFixed(2)}. Total deductions: $${Number(calc.agentFeeAmount || 0).toFixed(2)} + $${Number(calc.otherDeductions || 0).toFixed(2)} = $${Number(calc.totalDeductions || 0).toFixed(2)}. Net owner: $${Number(calc.grossRentPerPeriod || 0).toFixed(2)} − $${Number(calc.totalDeductions || 0).toFixed(2)} = $${Number(calc.netOwnerAmount || 0).toFixed(2)}.`;
};

const isRentSchedulePaid = (row) => {
  const status = String(row?.status || '').toLowerCase();
  return status === 'paid' || Boolean(row?.paidToDate);
};

const getOtherDeductionsAmount = (row) => Number(row?.otherFees ?? row?.otherDeductions ?? 0) || 0;

const getTotalDeductionsAmount = (row) => {
  return Math.abs(Number(row?.agentFeeAmount || 0)) + Math.abs(getOtherDeductionsAmount(row));
};

const getStartOfTodayTimestamp = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today.getTime();
};

const SummaryMetricCard = ({ label, value, supportingText, icon, tone = 'slate', onClick, active = false }) => {
  const toneStyles = {
    emerald: { label: 'text-emerald-700', value: 'text-emerald-700', icon: 'bg-emerald-50 text-emerald-600' },
    red: { label: 'text-red-600', value: 'text-red-600', icon: 'bg-red-50 text-red-500' },
    blue: { label: 'text-blue-600', value: 'text-blue-700', icon: 'bg-blue-50 text-blue-600' },
    violet: { label: 'text-violet-600', value: 'text-slate-900', icon: 'bg-violet-50 text-violet-600' },
    amber: { label: 'text-amber-600', value: 'text-amber-600', icon: 'bg-amber-50 text-amber-600' },
    slate: { label: 'text-slate-500', value: 'text-slate-900', icon: 'bg-slate-50 text-slate-500' }
  };
  const styles = toneStyles[tone] || toneStyles.slate;
  const className = `min-w-0 rounded-xl border p-3.5 sm:p-4 shadow-sm transition-colors ${onClick ? 'w-full text-left hover:border-amber-300 hover:bg-amber-50/40 focus:outline-none focus:ring-2 focus:ring-amber-400' : ''} ${active ? 'border-amber-400 bg-amber-50/60 ring-2 ring-amber-200' : 'border-slate-200 bg-white'}`;
  const content = (
    <>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className={`text-[11px] sm:text-xs font-semibold ${styles.label}`}>{label}</div>
          <div className={`mt-1 text-lg sm:text-xl font-bold tracking-tight truncate ${styles.value}`}>{value}</div>
          <div className="mt-1 text-[10px] sm:text-[11px] text-slate-400 truncate">{supportingText}</div>
        </div>
        <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${styles.icon}`}>
          <SafeIcon icon={icon} className="text-sm" />
        </span>
      </div>
      {onClick && <div className={`mt-2 text-[10px] font-semibold ${active ? 'text-amber-700' : 'text-slate-400'}`}>{active ? 'Filter active · click to clear' : 'Click to filter rows'}</div>}
    </>
  );

  return onClick ? (
    <button type="button" onClick={onClick} className={className} aria-pressed={active}>
      {content}
    </button>
  ) : (
    <div className={className}>
      {content}
    </div>
  );
};

const GenerationProgressPanel = ({ progress }) => {
  const total = Math.max(0, Number(progress?.total) || 0);
  const completed = Math.min(total, Math.max(0, Number(progress?.completed) || 0));
  const percent = Math.min(100, Math.max(0, Number(progress?.percent) || 0));
  const stage = progress?.stage || 'preparing';
  const stageLabel = stage === 'complete'
    ? 'Schedule saved successfully'
    : stage === 'partial'
      ? 'Schedule completed with some save errors'
      : stage === 'saving'
      ? 'Saving rent schedule to PocketBase'
      : 'Preparing rent schedule';

  return (
    <div className="absolute inset-0 z-40 flex items-center justify-center rounded-xl bg-white/95 px-4 py-8 backdrop-blur-[1px]">
      <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-xl" role="status" aria-live="polite" aria-busy={!['complete', 'partial'].includes(stage)}>
        <div className="flex items-start gap-3">
          <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${stage === 'complete' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
            <SafeIcon icon={stage === 'complete' ? 'CheckCircle' : stage === 'partial' ? 'AlertCircle' : 'Loader'} className={`text-lg ${stage === 'complete' || stage === 'partial' ? '' : 'animate-spin'}`} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-slate-900">{stageLabel}</div>
            <div className="mt-1 text-xs leading-5 text-slate-500">
              {progress?.message || 'Preparing the schedule for saving.'}
            </div>
          </div>
          <div className="text-sm font-bold tabular-nums text-slate-900">{percent}%</div>
        </div>

        <div className="mt-5 h-2.5 overflow-hidden rounded-full bg-slate-100">
          <div
            className={`h-full rounded-full transition-[width] duration-300 ease-out ${stage === 'complete' ? 'bg-emerald-500' : 'bg-amber-500'}`}
            style={{ width: `${percent}%` }}
          />
        </div>

        <div className="mt-3 flex items-center justify-between gap-3 text-xs text-slate-500">
          <span>{total > 0 ? `${completed} of ${total} items saved` : 'Preparing items…'}</span>
          <span className="font-medium text-slate-600">Please keep this window open</span>
        </div>

        <div className="mt-4 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-[11px] leading-5 text-slate-500">
          Controls are temporarily locked while the schedule is being written. You can continue once this step finishes.
        </div>
      </div>
    </div>
  );
};

const BulkPaidProgressPanel = ({ progress }) => {
  const total = Math.max(0, Number(progress?.total) || 0);
  const completed = Math.min(total, Math.max(0, Number(progress?.completed) || 0));
  const succeeded = Math.max(0, Number(progress?.succeeded) || 0);
  const failed = Math.max(0, Number(progress?.failed) || 0);
  const percent = Math.min(100, Math.max(0, Number(progress?.percent) || 0));
  const isComplete = progress?.stage === 'complete' || progress?.stage === 'partial';
  const isPartial = progress?.stage === 'partial';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="bulk-paid-progress-title" aria-busy={!isComplete}>
      <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl sm:p-6">
        <div className="flex items-start gap-3">
          <div className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full ${isComplete ? (isPartial ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600') : 'bg-amber-50 text-amber-600'}`}>
            <SafeIcon icon={isComplete ? (isPartial ? 'AlertCircle' : 'CheckCircle') : 'Loader'} className={`text-xl ${isComplete ? '' : 'animate-spin'}`} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 id="bulk-paid-progress-title" className="text-lg font-semibold text-slate-900">{isComplete ? (isPartial ? 'Bulk update finished with errors' : 'Bulk update complete') : 'Marking past-due rent as paid'}</h3>
            <p className="mt-1 text-sm leading-5 text-slate-600">{progress?.message || 'Updating rent schedule records one at a time.'}</p>
          </div>
          <div className="text-sm font-bold tabular-nums text-slate-900">{percent}%</div>
        </div>
        <div className="mt-5 h-3 overflow-hidden rounded-full bg-slate-100">
          <div className={`h-full rounded-full transition-[width] duration-300 ease-out ${isPartial ? 'bg-amber-500' : isComplete ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{ width: `${percent}%` }} />
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
          <span>{completed} of {total} records processed</span>
          <span><span className="font-semibold text-emerald-700">{succeeded} saved</span>{failed > 0 && <span className="font-semibold text-red-600"> · {failed} failed</span>}</span>
        </div>
        <div className="mt-4 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-[11px] leading-5 text-slate-500">
          The page is temporarily locked while PocketBase records are updated at approximately one record per second. Please keep this window open.
        </div>
      </div>
    </div>
  );
};

const PaymentActionModal = ({ action, scheduleRow, onSubmit, onCancel }) => {
  const [paymentAmount, setPaymentAmount] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  const actions = {
    paid: { title: 'Mark as Paid', button: 'Confirm Paid', color: 'bg-emerald-600' },
    partial: { title: 'Mark as Partial', button: 'Confirm Partial', color: 'bg-amber-600' },
    unpaid: { title: 'Mark as Unpaid', button: 'Confirm Unpaid', color: 'bg-slate-600' },
    waived: { title: 'Mark as Waived', button: 'Confirm Waived', color: 'bg-indigo-600' },
    disputed: { title: 'Mark as Disputed', button: 'Confirm Disputed', color: 'bg-red-600' }
  };

  const currentAction = actions[action];
  if (!currentAction) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      action,
      paymentAmount: action === 'partial' ? parseFloat(paymentAmount) || 0 : null,
      reason,
      notes
    });
  };

  const inputClass = "w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white";
  const labelClass = "block text-xs font-medium text-slate-600 mb-1";

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
        <div className="text-sm text-slate-600">Period Due</div>
        <div className="font-semibold text-slate-900">
          {scheduleRow ? calculations.formatDateAU(scheduleRow.dueDate) : '-'}
        </div>
        <div className="text-sm text-slate-600 mt-2">Current Status</div>
        <div className="font-semibold text-slate-900">{scheduleRow?.status || '-'}</div>
        <div className="text-sm text-slate-600 mt-2">Gross Rent</div>
        <div className="font-semibold text-slate-900">
          ${scheduleRow?.grossRentDue?.toFixed(2) || '0.00'}
        </div>
      </div>
      {action === 'partial' && (
        <div>
          <label className={labelClass}>Payment Amount ($)</label>
          <input
            type="number"
            value={paymentAmount}
            onChange={(e) => setPaymentAmount(e.target.value)}
            className={inputClass}
            step="0.01"
            min="0.01"
            max={scheduleRow?.grossRentDue || 0}
            placeholder="Enter amount received"
            required
          />
        </div>
      )}
      <div>
        <label className={labelClass}>Reason</label>
        <select value={reason} onChange={(e) => setReason(e.target.value)} className={inputClass}>
          <option value="">Select reason...</option>
          <option value="Standard Payment">Standard Payment</option>
          <option value="Bank Transfer">Bank Transfer</option>
          <option value="Direct Debit">Direct Debit</option>
          <option value="Cash">Cash</option>
          <option value="Cheque">Cheque</option>
          <option value="Adjustment">Adjustment</option>
          <option value="Other">Other</option>
        </select>
      </div>
      <div>
        <label className={labelClass}>Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className={inputClass}
          rows="3"
          placeholder="Additional notes..."
        />
      </div>
      <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">
          Cancel
        </button>
        <button type="submit" className={`px-4 py-2 text-sm font-medium text-white rounded-lg ${currentAction.color} hover:opacity-90`}>
          {currentAction.button}
        </button>
      </div>
    </form>
  );
};

const RegenerateConfirmModal = ({ lease, newRent, effectiveDate, onConfirm, onCancel }) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    onConfirm();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-amber-800 mb-2">Important Notice</h4>
            <p className="text-sm text-amber-700">
              Regenerating the schedule will update all <strong>future unpaid periods</strong> with the new rent amount. Historical paid rows will remain unchanged to preserve accurate records.
            </p>
          </div>
        </div>
      </div>
      <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 space-y-3">
        <div className="flex justify-between text-sm">
          <span className="text-slate-600">Current Weekly Rent:</span>
          <span className="font-medium">${lease?.weeklyRent?.toFixed(2) || '0.00'}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-600">New Weekly Rent:</span>
          <span className="font-medium text-emerald-600">${newRent?.toFixed(2) || '0.00'}</span>
        </div>
        <div className="flex justify-between text-sm pt-2 border-t">
          <span className="text-slate-600">Effective Date:</span>
          <span className="font-medium">{effectiveDate ? calculations.formatDateAU(effectiveDate) : '-'}</span>
        </div>
      </div>
      <div className="text-xs text-slate-500">This action cannot be undone. Make sure the rent increase has been properly recorded first.</div>
      <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">
          Cancel
        </button>
        <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-lg">
          Regenerate Future Schedule
        </button>
      </div>
    </form>
  );
};

const RentSchedulesPage = () => {
  const { showToast, showConfirm, showStagedMessages } = useFeedback();
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isRegenerateModalOpen, setIsRegenerateModalOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [fyFilter, setFyFilter] = useState('All');
  const [selectedPropertyId, setSelectedPropertyId] = useState('All');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(null);
  const [markingPaidId, setMarkingPaidId] = useState(null);
  const [paidConfirmation, setPaidConfirmation] = useState(null);
  const [paidOverrides, setPaidOverrides] = useState({});
  const [bulkPaidProgress, setBulkPaidProgress] = useState(null);
  const [summaryFilter, setSummaryFilter] = useState('all');
  const confirmationTimerRef = useRef(null);

  useEffect(() => {
    const handleDataRefreshed = () => setRefreshTrigger(prev => prev + 1);
    window.addEventListener('mpp:data-refreshed', handleDataRefreshed);
    return () => window.removeEventListener('mpp:data-refreshed', handleDataRefreshed);
  }, []);

  useEffect(() => {
    return () => {
      if (confirmationTimerRef.current) {
        clearTimeout(confirmationTimerRef.current);
      }
    };
  }, []);

  const getScheduleLinkedLease = (row) => {
    return row?.leaseId ? demoRepository.getLeaseById(row.leaseId) : null;
  };

  const getSchedulePropertyId = (row, lease = null) => {
    return row?.propertyId || lease?.propertyId || '';
  };

  const getScheduleSpaceId = (row, lease = null) => {
    return row?.spaceId || lease?.spaceId || '';
  };

  const getScheduleTenantId = (row, lease = null) => {
    return row?.tenantId || lease?.tenantId || '';
  };

  const getScheduleTenantDisplayName = (row, tenant = null) => {
    if (row?.tenantDisplayMode === 'private' || row?.metadata?.tenantDisplayMode === 'private') return 'Private / withheld';
    if (tenant) return `${tenant.firstName || ''} ${tenant.lastName || ''}`.trim() || tenant.name || 'Tenant';
    return row?.tenantDisplayName || row?.metadata?.tenantDisplayName || 'Tenant not assigned';
  };

  const allSchedules = useMemo(() => {
    return demoRepository.getRentSchedules().map(row => paidOverrides[row.id] ? { ...row, ...paidOverrides[row.id] } : row);
  }, [refreshTrigger, paidOverrides]);

  const propertyChipData = useMemo(() => {
    const propertyCounts = new Map();

    allSchedules.forEach(row => {
      const lease = getScheduleLinkedLease(row);
      const propertyId = getSchedulePropertyId(row, lease);
      if (!propertyId) return;

      const existing = propertyCounts.get(propertyId) || {
        id: propertyId,
        count: 0,
        fallbackName: row?.metadata?.propertyName || ''
      };
      existing.count += 1;
      if (!existing.fallbackName && row?.metadata?.propertyName) {
        existing.fallbackName = row.metadata.propertyName;
      }
      propertyCounts.set(propertyId, existing);
    });

    return [...propertyCounts.values()]
      .map(item => ({
        id: item.id,
        count: item.count,
        name: demoRepository.getPropertyById(item.id)?.name || item.fallbackName || 'Unknown Property'
      }))
      .sort((a, b) => a.name.localeCompare(b.name, 'en-AU', { sensitivity: 'base' }));
  }, [allSchedules]);

  useEffect(() => {
    if (selectedPropertyId !== 'All' && !propertyChipData.some(property => property.id === selectedPropertyId)) {
      setSelectedPropertyId('All');
    }
  }, [selectedPropertyId, propertyChipData]);

  const filteredSchedules = useMemo(() => {
    return allSchedules.filter(row => {
      const lease = getScheduleLinkedLease(row);
      const propertyId = getSchedulePropertyId(row, lease);
      if (selectedPropertyId !== 'All' && propertyId !== selectedPropertyId) return false;
      if (statusFilter !== 'All' && row.status !== statusFilter) return false;
      if (fyFilter !== 'All' && row.financialYear !== fyFilter) return false;
      if (summaryFilter === 'agentFee' && Number(row?.agentFeeAmount || 0) <= 0) return false;
      if (summaryFilter === 'otherDeductions' && getOtherDeductionsAmount(row) <= 0) return false;
      if (summaryFilter === 'totalDeductions' && getTotalDeductionsAmount(row) <= 0) return false;
      if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        const tenantId = getScheduleTenantId(row, lease);
        const spaceId = getScheduleSpaceId(row, lease);
        const tenant = tenantId ? demoRepository.getTenantById(tenantId) : null;
        const property = propertyId ? demoRepository.getPropertyById(propertyId) : null;
        const space = spaceId ? demoRepository.getSpaceById(spaceId) : null;
        const tenantDisplayName = getScheduleTenantDisplayName(row, tenant);
        return (
          tenantDisplayName.toLowerCase().includes(lower) ||
          tenant?.firstName?.toLowerCase().includes(lower) ||
          tenant?.lastName?.toLowerCase().includes(lower) ||
          property?.name?.toLowerCase().includes(lower) ||
          property?.address?.toLowerCase().includes(lower) ||
          property?.suburb?.toLowerCase().includes(lower) ||
          space?.name?.toLowerCase().includes(lower) ||
          row?.tenantDisplayName?.toLowerCase?.().includes(lower) ||
          row?.metadata?.tenantDisplayName?.toLowerCase?.().includes(lower) ||
          row?.metadata?.propertyName?.toLowerCase?.().includes(lower) ||
          row?.metadata?.spaceName?.toLowerCase?.().includes(lower)
        );
      }
      return true;
    });
  }, [allSchedules, searchTerm, statusFilter, fyFilter, selectedPropertyId, summaryFilter]);

  const pastDueUnpaidRows = useMemo(() => {
    const todayStart = getStartOfTodayTimestamp();
    return filteredSchedules.filter(row => {
      const dueDate = new Date(row?.dueDate).getTime();
      const status = String(row?.status || '').toLowerCase();
      return Number.isFinite(dueDate) && dueDate < todayStart && (status === 'unpaid' || status === 'partial') && !row?.paidToDate;
    });
  }, [filteredSchedules]);

  const getScheduleSortValue = useCallback((row, key) => {
    const lease = getScheduleLinkedLease(row);
    const propertyId = getSchedulePropertyId(row, lease);
    const tenantId = getScheduleTenantId(row, lease);
    const property = propertyId ? demoRepository.getPropertyById(propertyId) : null;
    const tenant = tenantId ? demoRepository.getTenantById(tenantId) : null;

    switch (key) {
      case 'dueDate': return new Date(row?.dueDate || 0).getTime() || 0;
      case 'periodStart': return new Date(row?.periodStart || 0).getTime() || 0;
      case 'property': return String(property?.name || row?.metadata?.propertyName || '').toLowerCase();
      case 'tenant': return String(tenant ? `${tenant.firstName || ''} ${tenant.lastName || ''}`.trim() : getScheduleTenantDisplayName(row, tenant)).toLowerCase();
      case 'grossRent': return Number(row?.grossRentDue || 0);
      case 'agentFee': return Number(row?.agentFeeAmount || 0);
      case 'otherDeductions': return Number(row?.otherFees || 0);
      case 'totalDeductions': return Number(row?.totalDeductions || 0);
      case 'netOwner': return Number(row?.netOwnerAmount || 0);
      case 'paidStatus': return row?.paidToDate ? new Date(row.paidToDate).getTime() || 0 : 0;
      case 'status': return String(row?.status || '').toLowerCase();
      default: return '';
    }
  }, []);

  const sortedSchedules = useMemo(() => {
    if (!sortConfig.key) return filteredSchedules;
    const directionMultiplier = sortConfig.direction === 'desc' ? -1 : 1;
    return [...filteredSchedules].sort((a, b) => {
      const aValue = getScheduleSortValue(a, sortConfig.key);
      const bValue = getScheduleSortValue(b, sortConfig.key);
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        if (aValue === bValue) return 0;
        return (aValue - bValue) * directionMultiplier;
      }
      return String(aValue).localeCompare(String(bValue), 'en-AU', { numeric: true, sensitivity: 'base' }) * directionMultiplier;
    });
  }, [filteredSchedules, sortConfig, getScheduleSortValue]);

  const uniqueFinancialYears = useMemo(() => {
    const years = [...new Set(allSchedules.map(s => s.financialYear))].filter(Boolean);
    return ['All', ...years];
  }, [allSchedules]);

  const selectedPropertyLabel = useMemo(() => {
    if (selectedPropertyId === 'All') return 'All Properties';
    return propertyChipData.find(property => property.id === selectedPropertyId)?.name || 'Selected Property';
  }, [selectedPropertyId, propertyChipData]);

  const financialSummary = useMemo(() => {
    const validPropertyIds = new Set(propertyChipData.map(property => String(property.id)));
    const selectedId = selectedPropertyId === 'All' ? null : String(selectedPropertyId);
    const now = Date.now();
    const upcomingCutoff = now + (30 * 24 * 60 * 60 * 1000);

    const scopedSchedules = allSchedules.filter(row => {
      const lease = getScheduleLinkedLease(row);
      const propertyId = String(getSchedulePropertyId(row, lease) || '');
      if (!propertyId) return false;
      if (selectedId ? propertyId !== selectedId : !validPropertyIds.has(propertyId)) return false;
      if (fyFilter !== 'All' && row.financialYear !== fyFilter) return false;
      return true;
    });

    const paidSchedules = scopedSchedules.filter(isRentSchedulePaid);
    const totalRentCollected = paidSchedules.reduce((sum, row) => sum + Math.abs(Number(row?.grossRentDue || row?.grossRent || 0)), 0);
    const totalAgentFees = paidSchedules.reduce((sum, row) => sum + Math.abs(Number(row?.agentFeeAmount || 0)), 0);
    const totalOtherDeductions = paidSchedules.reduce((sum, row) => sum + Math.abs(getOtherDeductionsAmount(row)), 0);
    const totalDeductions = paidSchedules.reduce((sum, row) => sum + getTotalDeductionsAmount(row), 0);

    const dueRows = scopedSchedules.filter(row => {
      const dueDate = new Date(row?.dueDate).getTime();
      const status = String(row?.status || '').toLowerCase();
      return Number.isFinite(dueDate) && dueDate <= now && !['waived', 'archived'].includes(status);
    });

    const dueGross = dueRows.reduce((sum, row) => sum + Math.abs(Number(row?.grossRentDue || row?.grossRent || 0)), 0);
    const paidDueGross = dueRows.reduce((sum, row) => {
      return isRentSchedulePaid(row) ? sum + Math.abs(Number(row?.grossRentDue || row?.grossRent || 0)) : sum;
    }, 0);

    const upcomingReceipts = scopedSchedules.reduce((sum, row) => {
      const dueDate = new Date(row?.dueDate).getTime();
      const status = String(row?.status || '').toLowerCase();
      if (!Number.isFinite(dueDate) || dueDate <= now || dueDate > upcomingCutoff) return sum;
      if (['paid', 'waived', 'archived'].includes(status)) return sum;
      return sum + Math.abs(Number(row?.netOwnerAmount || 0));
    }, 0);

    const roundMoney = (value) => Math.round((Number(value) || 0) * 100) / 100;
    const collectionRate = dueGross > 0 ? Math.min(100, (paidDueGross / dueGross) * 100) : 0;

    return {
      totalRentCollected: roundMoney(totalRentCollected),
      totalAgentFees: roundMoney(totalAgentFees),
      totalOtherDeductions: roundMoney(totalOtherDeductions),
      totalDeductions: roundMoney(totalDeductions),
      totalExpenses: roundMoney(totalDeductions),
      netIncome: roundMoney(totalRentCollected - totalDeductions),
      collectionRate,
      upcomingReceipts: roundMoney(upcomingReceipts)
    };
  }, [allSchedules, propertyChipData, selectedPropertyId, fyFilter, refreshTrigger]);

  const handleSort = useCallback((key) => {
    setSortConfig(current => {
      if (current.key === key) {
        return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: key === 'dueDate' || key === 'periodStart' || key === 'paidStatus' ? 'desc' : 'asc' };
    });
  }, []);

  const renderSortableHeader = (label, key) => (
    <button
      type="button"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        handleSort(key);
      }}
      className="inline-flex items-center gap-1.5 whitespace-nowrap text-left hover:text-slate-900 focus:outline-none focus:text-slate-900"
      title={`Sort by ${label}`}
      aria-label={`Sort by ${label}${sortConfig.key === key ? `, currently ${sortConfig.direction}ending` : ''}`}
    >
      <span>{label}</span>
      <span className={`text-[10px] leading-none ${sortConfig.key === key ? 'text-amber-600' : 'text-slate-300'}`} aria-hidden="true">
        {sortConfig.key === key ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '↕'}
      </span>
    </button>
  );

  const handleGenerateSchedule = useCallback(async (data) => {
    const totalRows = Array.isArray(data?.scheduleRows) ? data.scheduleRows.length : 0;
    setIsGenerating(true);
    setGenerationProgress({
      stage: 'preparing',
      total: totalRows,
      completed: 0,
      percent: 0,
      message: totalRows > 0 ? `Preparing ${totalRows} rent schedule item${totalRows === 1 ? '' : 's'} for saving…` : 'Preparing rent schedule…'
    });

    try {
      const sourceLease = data.lease || {};
      const propertyId = data.propertyId || sourceLease.propertyId || '';
      const spaceId = data.spaceId || sourceLease.spaceId || '';
      const tenantId = data.tenantId || sourceLease.tenantId || '';
      const leaseId = data.leaseId || sourceLease.id || '';
      const scheduleSource = data.scheduleSource || sourceLease.sourceType || sourceLease.metadata?.scheduleSource || 'lease';
      const tenantDisplayName = data.tenantDisplayName || sourceLease.tenantDisplayName || sourceLease.metadata?.tenantDisplayName || '';

      if (!propertyId) {
        showToast('Rent schedule could not be generated because property is missing.', 'error');
        return;
      }

      const scheduleRows = data.scheduleRows.map((row, index) => ({
        ...row,
        leaseId,
        propertyId,
        spaceId,
        tenantId,
        tenantDisplayName: tenantDisplayName || (tenantId ? '' : 'Tenant not assigned'),
        tenantDisplayMode: tenantId ? '' : 'not_assigned',
        scheduleSource,
        agentFeePercent: data.agentFeePercent,
        otherDeductions: data.otherDeductions,
        periodNumber: index + 1,
        metadata: {
          ...(row.metadata || {}),
          scheduleSource,
          tenantDisplayName: tenantDisplayName || (tenantId ? '' : 'Tenant not assigned'),
          tenantDisplayMode: tenantId ? '' : 'not_assigned',
          propertyId,
          spaceId,
          tenantId,
          leaseId
        }
      }));

      const result = await demoRepository.addRentScheduleRows(scheduleRows, {
        onProgress: ({ current, total, mode }) => {
          const safeTotal = Math.max(1, Number(total) || scheduleRows.length || 1);
          const savingIndex = Math.min(safeTotal, Math.max(1, Number(current) || 1));
          const completed = Math.max(0, savingIndex - 1);
          const percent = Math.min(95, Math.round((completed / safeTotal) * 95));
          setGenerationProgress({
            stage: 'saving',
            total: safeTotal,
            completed,
            percent,
            message: `${mode === 'pocketbase' ? 'Saving to PocketBase' : 'Saving'} — item ${savingIndex} of ${safeTotal}`
          });
        }
      });

      if (result.success) {
        const savedCount = result.count || result.savedCount || scheduleRows.length;
        const failedCount = Number(result.failedCount || 0);
        setGenerationProgress({
          stage: failedCount > 0 ? 'partial' : 'complete',
          total: scheduleRows.length,
          completed: savedCount,
          percent: 100,
          message: failedCount > 0
            ? `${savedCount} item${savedCount === 1 ? '' : 's'} saved and ${failedCount} failed.`
            : `${savedCount} rent schedule item${savedCount === 1 ? '' : 's'} saved successfully.`
        });

        if (failedCount > 0) {
          showToast(`${savedCount} rent schedule rows saved and ${failedCount} failed. ${result.errors?.[0] || ''}`.trim(), 'warning');
        } else if (result.mode === 'pocketbase') {
          showToast(`${savedCount} rent schedule rows saved to PocketBase.`, 'success');
        } else if (result.mode === 'demo') {
          showToast('Rent schedule added to demo data.', 'success');
        } else {
          showToast('Rent schedule generated successfully.', 'success');
        }

        await new Promise(resolve => setTimeout(resolve, 350));
        setIsGenerateModalOpen(false);
        setRefreshTrigger(prev => prev + 1);
      } else {
        showToast(`Rent schedule was not saved: ${result.error || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      showToast(`Error generating schedule: ${error.message}`, 'error');
    } finally {
      setIsGenerating(false);
      setGenerationProgress(null);
    }
  }, [showToast]);

  const handlePaymentAction = useCallback(async (data) => {
    if (!selectedRow) return;
    let payload = {};
    switch (data.action) {
      case 'paid':
        payload = { status: 'Paid', paidToDate: Date.now(), lastPaymentDate: Date.now(), paymentNotes: data.notes, paymentReason: data.reason };
        break;
      case 'partial':
        payload = { status: 'Partial', lastPaymentDate: Date.now(), paymentNotes: data.notes, paymentReason: data.reason };
        break;
      case 'unpaid':
        payload = { status: 'Unpaid', paidToDate: null, lastPaymentDate: null, paymentNotes: data.notes, paymentReason: data.reason };
        break;
      case 'waived':
        payload = { status: 'Waived', paymentNotes: data.notes, paymentReason: data.reason };
        break;
      case 'disputed':
        payload = { status: 'Disputed', paymentNotes: data.notes, paymentReason: data.reason };
        break;
      default:
        break;
    }

    const result = await demoRepository.updateRentSchedule(selectedRow.id, { ...selectedRow, ...payload });
    if (!result?.success) {
      showToast(result?.error || 'Payment action failed.', 'error');
      return;
    }

    if (data.action === 'paid') {
      const paidAt = result.record?.paidToDate || payload.paidToDate || Date.now();
      setPaidOverrides(current => ({ ...current, [selectedRow.id]: { status: 'Paid', paidToDate: paidAt } }));
    } else {
      setPaidOverrides(current => {
        if (!current[selectedRow.id]) return current;
        const next = { ...current };
        delete next[selectedRow.id];
        return next;
      });
    }

    const actionLabels = {
      paid: 'marked as paid',
      partial: 'marked as partial',
      unpaid: 'marked as unpaid',
      waived: 'marked as waived',
      disputed: 'marked as disputed'
    };
    showToast(`Row ${actionLabels[data.action]}`, 'success');
    setIsPaymentModalOpen(false);
    setSelectedRow(null);
    setSelectedAction(null);
    setRefreshTrigger(prev => prev + 1);
  }, [selectedRow, showToast]);

  const handleBulkMarkPaid = useCallback(() => {
    const rowsToMark = pastDueUnpaidRows;
    if (rowsToMark.length === 0) {
      showToast('There are no unpaid or partial rent rows due before today.', 'info');
      return;
    }

    const persistenceMode = pocketbaseService.getActiveDataSource?.() || 'demo';
    const persistenceLabel = String(persistenceMode).startsWith('backend') ? 'PocketBase' : 'saved workspace data';
    showConfirm({
      title: `Mark ${rowsToMark.length} Past-Due Rent Payments as Paid?`,
      message: `${rowsToMark.length} unpaid or partial rent payment${rowsToMark.length === 1 ? '' : 's'} with a due date before today will be updated. The records will be written to ${persistenceLabel} one at a time at approximately one per second.`,
      confirmText: 'Mark as Paid',
      cancelText: 'Cancel',
      isDestructive: false,
      onConfirm: async () => {
        const total = rowsToMark.length;
        let succeeded = 0;
        let failed = 0;
        let firstError = null;
        const successfulPaid = {};
        setBulkPaidProgress({
          stage: 'saving',
          total,
          completed: 0,
          succeeded: 0,
          failed: 0,
          percent: 0,
          message: `Starting ${total} past-due payment update${total === 1 ? '' : 's'}…`
        });

        for (let index = 0; index < rowsToMark.length; index++) {
          const row = rowsToMark[index];
          const paidAt = Date.now();
          setBulkPaidProgress(current => ({
            ...(current || {}),
            stage: 'saving',
            message: `Updating payment ${index + 1} of ${total}…`
          }));

          const result = await demoRepository.updateRentSchedule(row.id, {
            ...row,
            status: 'Paid',
            paidToDate: paidAt,
            lastPaymentDate: paidAt
          });

          if (result?.success) {
            succeeded += 1;
            successfulPaid[row.id] = { status: 'Paid', paidToDate: result.record?.paidToDate || paidAt };
          } else {
            failed += 1;
            if (!firstError) firstError = result?.error || 'Unknown error';
          }

          const completed = index + 1;
          setBulkPaidProgress(current => ({
            ...(current || {}),
            completed,
            succeeded,
            failed,
            percent: Math.round((completed / total) * 100),
            message: result?.success
              ? `Payment ${completed} of ${total} saved.`
              : `Payment ${completed} of ${total} could not be saved. Continuing safely…`
          }));

          if (index < rowsToMark.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }

        if (Object.keys(successfulPaid).length > 0) {
          setPaidOverrides(current => ({ ...current, ...successfulPaid }));
        }
        setRefreshTrigger(prev => prev + 1);
        setBulkPaidProgress({
          stage: failed > 0 ? 'partial' : 'complete',
          total,
          completed: total,
          succeeded,
          failed,
          percent: 100,
          message: failed > 0
            ? `${succeeded} payment${succeeded === 1 ? '' : 's'} saved. ${failed} failed. ${firstError || ''}`.trim()
            : `${succeeded} past-due payment${succeeded === 1 ? '' : 's'} marked as paid successfully.`
        });
        showToast(
          failed > 0
            ? `${succeeded} of ${total} past-due payments marked as paid. ${failed} failed.`
            : `${succeeded} past-due payments marked as paid.`,
          failed > 0 ? 'error' : 'success'
        );
        setTimeout(() => setBulkPaidProgress(null), failed > 0 ? 2200 : 1200);
      }
    });
  }, [pastDueUnpaidRows, showConfirm, showToast]);

  const handleQuickMarkPaid = useCallback(async (row) => {
    if (!row || row.status === 'Paid' || markingPaidId === row.id) return;
    setMarkingPaidId(row.id);
    const paidAt = Date.now();
    try {
      const result = await demoRepository.updateRentSchedule(row.id, {
        ...row,
        status: 'Paid',
        paidToDate: paidAt,
        lastPaymentDate: paidAt
      });
      if (!result?.success) {
        showToast(result?.error || 'Payment action failed.', 'error');
        return;
      }
      setPaidOverrides(current => ({
        ...current,
        [row.id]: {
          status: 'Paid',
          paidToDate: result.record?.paidToDate || paidAt
        }
      }));
      setRefreshTrigger(prev => prev + 1);
      if (confirmationTimerRef.current) {
        clearTimeout(confirmationTimerRef.current);
      }
      setPaidConfirmation({ id: row.id, message: 'Rent marked as paid · figures updated' });
      confirmationTimerRef.current = setTimeout(() => {
        setPaidConfirmation(null);
        confirmationTimerRef.current = null;
      }, 1800);
    } catch (error) {
      showToast(error?.message || 'Payment action failed.', 'error');
    } finally {
      setMarkingPaidId(null);
    }
  }, [markingPaidId, showToast]);

  const handleRegenerateSchedule = useCallback((data) => {
    const lease = demoRepository.getLeaseById(data.leaseId);
    if (!lease) return;
    setSelectedRow({ lease, newRent: data.newRent, effectiveDate: data.effectiveDate });
    setIsRegenerateModalOpen(true);
  }, []);

  const confirmRegenerate = useCallback(async () => {
    if (!selectedRow) return;
    const { lease, newRent, effectiveDate } = selectedRow;
    const leaseRows = allSchedules.filter(r => r.leaseId === lease.id);
    const effectiveTimestamp = new Date(effectiveDate).getTime();
    const updatedRows = rentScheduleService.applyRentIncrease(
      leaseRows,
      newRent,
      effectiveDate,
      lease.rentBasis || 'weekly',
      lease.paymentFrequency || 'weekly'
    );
    let firstError = null;
    for (const row of updatedRows) {
      if (row.status === 'Paid' || row.dueDate < effectiveTimestamp) continue;
      const existingRow = leaseRows.find(existing => existing.id === row.id) || row;
      const result = await demoRepository.updateRentSchedule(row.id, {
        ...existingRow,
        grossRentDue: row.grossRentDue,
        agentFeeAmount: row.agentFeeAmount,
        totalDeductions: row.totalDeductions,
        netOwnerAmount: row.netOwnerAmount,
        metadata: row.metadata
      });
      if (!result?.success && !firstError) {
        firstError = result?.error || 'Unknown error';
      }
    }
    const leaseResult = await demoRepository.updateLease(lease.id, { ...lease, weeklyRent: newRent });
    if (firstError) {
      showToast(`Regeneration partially failed: ${firstError}`, 'error');
    } else if (!leaseResult?.success) {
      showToast(`Schedule updated but lease rent update failed: ${leaseResult?.error || 'Unknown error'}`, 'error');
    } else {
      showToast('Schedule regenerated with new rent', 'success');
    }
    setIsRegenerateModalOpen(false);
    setSelectedRow(null);
    setRefreshTrigger(prev => prev + 1);
  }, [selectedRow, allSchedules, showToast]);

  const handleAddDeduction = useCallback(async (rowId, amount, reason) => {
    const row = allSchedules.find(r => r.id === rowId);
    if (!row) return;
    const newOtherFees = (row.otherFees || 0) + (parseFloat(amount) || 0);
    const newTotalDeductions = rentScheduleService.calculateTotalDeductions(row.agentFeeAmount, newOtherFees);
    const newNetOwnerAmount = rentScheduleService.calculateNetOwnerAmount(row.grossRentDue, newTotalDeductions);
    const updatedMetadata = row.metadata?.rentCalculation ? {
      ...(row.metadata || {}),
      rentCalculation: {
        ...row.metadata.rentCalculation,
        otherDeductions: newOtherFees,
        totalDeductions: newTotalDeductions,
        netOwnerAmount: newNetOwnerAmount
      }
    } : row.metadata;
    const result = await demoRepository.updateRentSchedule(rowId, {
      ...row,
      otherFees: newOtherFees,
      otherDeductions: newOtherFees,
      totalDeductions: newTotalDeductions,
      netOwnerAmount: newNetOwnerAmount,
      deductionReason: reason,
      metadata: updatedMetadata
    });
    if (!result?.success) {
      showToast(result?.error || 'Deduction add failed.', 'error');
      return;
    }
    showToast('Deduction added', 'success');
    setRefreshTrigger(prev => prev + 1);
  }, [allSchedules, showToast]);

  const handleDuplicateRow = useCallback(async (row) => {
    const linkedLease = getScheduleLinkedLease(row);
    const leaseTerminated = String(linkedLease?.status || '').toLowerCase() === 'terminated';
    if (leaseTerminated || ['Waived', 'Archived'].includes(row.status)) {
      showToast('Waived or terminated-lease schedule rows cannot be duplicated. Create the replacement tenant lease instead.', 'warning');
      return;
    }
    const newRow = {
      ...row,
      id: undefined,
      periodNumber: row.periodNumber + 1,
      status: 'Unpaid',
      paidToDate: null
    };
    const result = await demoRepository.addRentScheduleRows([newRow]);
    if (!result?.success) {
      showToast(result?.error || 'Row duplication failed.', 'error');
      return;
    }
    showToast('Row duplicated', 'success');
    setRefreshTrigger(prev => prev + 1);
  }, [showToast]);

  const getPaymentActions = (row) => {
    const actions = [];
    const protectedFromPaid = row.status === 'Waived' || row.status === 'Archived';
    if (row.status !== 'Paid' && !protectedFromPaid) actions.push({ key: 'paid', label: 'Mark Paid', color: 'text-emerald-600' });
    if (row.status !== 'Partial') actions.push({ key: 'partial', label: 'Mark Partial', color: 'text-amber-600' });
    if (row.status !== 'Unpaid') actions.push({ key: 'unpaid', label: 'Mark Unpaid', color: 'text-slate-600' });
    if (row.status !== 'Waived') actions.push({ key: 'waived', label: 'Mark Waived', color: 'text-indigo-600' });
    if (row.status !== 'Disputed') actions.push({ key: 'disputed', label: 'Mark Disputed', color: 'text-red-600' });
    return actions;
  };

  const columns = [
    renderSortableHeader('Due Date', 'dueDate'),
    renderSortableHeader('Rent Period Covered', 'periodStart'),
    renderSortableHeader('Property', 'property'),
    renderSortableHeader('Tenant', 'tenant'),
    renderSortableHeader('Gross Rent', 'grossRent'),
    renderSortableHeader('Agent Fee', 'agentFee'),
    renderSortableHeader('Other Deductions', 'otherDeductions'),
    renderSortableHeader('Total Deductions', 'totalDeductions'),
    renderSortableHeader('Net Owner', 'netOwner'),
    renderSortableHeader('Paid Status', 'paidStatus'),
    renderSortableHeader('Status', 'status'),
    'Actions'
  ];

  const getRowContext = (row) => {
    const lease = getScheduleLinkedLease(row);
    const tenantId = getScheduleTenantId(row, lease);
    const propertyId = getSchedulePropertyId(row, lease);
    const spaceId = getScheduleSpaceId(row, lease);
    const tenant = tenantId ? demoRepository.getTenantById(tenantId) : null;
    const property = propertyId ? demoRepository.getPropertyById(propertyId) : null;
    const space = spaceId ? demoRepository.getSpaceById(spaceId) : null;
    const tenantDisplayName = getScheduleTenantDisplayName(row, tenant);
    return { lease, tenantId, propertyId, spaceId, tenant, property, space, tenantDisplayName };
  };

  const renderRow = (row) => {
    const { property, space, tenant, tenantDisplayName } = getRowContext(row);
    const calculationHelp = getScheduleCalculationHelp(row);
    const statusKey = String(row.status || '').toLowerCase();
    const isPaid = statusKey === 'paid' || Boolean(row.paidToDate);
    const isWaived = statusKey === 'waived';
    const isEarlyTerminationWaiver = isWaived && row?.metadata?.earlyTermination?.action === 'waived_future_period';
    const canQuickMarkPaid = statusKey === 'unpaid' || statusKey === 'partial';

    return [
      <div>
        <div className="font-medium text-slate-900">{calculations.formatDateAU(row.dueDate)}</div>
        <div className="text-xs text-slate-500">FY {row.financialYear}</div>
      </div>,
      <div>
        <div className="text-xs text-slate-600">{calculations.formatDateAU(row.periodStart)}</div>
        <div className="text-xs text-slate-500">to {calculations.formatDateAU(row.periodEnd)}</div>
      </div>,
      <div>
        <div className="font-medium text-slate-900">{property?.name || row?.metadata?.propertyName || '-'}</div>
        <div className="text-xs text-slate-500">{space?.name || row?.metadata?.spaceName || 'Whole Property'}</div>
      </div>,
      tenant ? `${tenant.firstName} ${tenant.lastName}` : tenantDisplayName === 'Tenant not assigned' ? '-' : tenantDisplayName,
      <div className="font-medium text-slate-900" title={calculationHelp}>${row.grossRentDue?.toFixed(2)}</div>,
      <div className="text-red-600" title={calculationHelp}>${row.agentFeeAmount?.toFixed(2)}</div>,
      <div className="text-red-600" title={calculationHelp}>${(row.otherFees || 0).toFixed(2)}</div>,
      <div className="font-medium text-red-600" title={calculationHelp}>${row.totalDeductions?.toFixed(2)}</div>,
      <div className="inline-flex items-center gap-1 font-semibold text-emerald-600" title={calculationHelp}>
        <span>${row.netOwnerAmount?.toFixed(2)}</span>
        <SafeIcon icon="Info" className="text-xs text-slate-400" />
      </div>,
      isPaid ? (
        <div className="inline-flex min-h-[32px] items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
          <SafeIcon icon="CheckCircle" />
          {row.paidToDate ? `Paid ${calculations.formatDateAU(row.paidToDate)}` : 'Paid'}
        </div>
      ) : isWaived ? (
        <div className="inline-flex min-h-[32px] items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700" title={isEarlyTerminationWaiver ? 'Future rent waived because this lease was terminated early.' : 'This rent period is waived and is not collectible.'}>
          <SafeIcon icon="MinusCircle" />
          {isEarlyTerminationWaiver ? 'Waived · lease ended' : 'Waived'}
        </div>
      ) : canQuickMarkPaid ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); handleQuickMarkPaid(row); }}
          disabled={markingPaidId === row.id}
          title="Mark this rent schedule as paid with one click"
          aria-label="Mark this rent schedule as paid"
          className="inline-flex min-h-[32px] items-center justify-center gap-1.5 rounded-lg border border-amber-300 bg-white px-2.5 py-1 text-xs font-semibold text-amber-700 transition-colors hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {markingPaidId === row.id ? <SafeIcon icon="Loader" className="animate-spin" /> : <SafeIcon icon="DollarSign" />}
          Mark as Paid
        </button>
      ) : (
        <span className="text-slate-400">—</span>
      ),
      <Badge label={row.status} status={row.status === 'Paid' ? 'active' : row.status === 'Unpaid' ? 'overdue' : row.status === 'Waived' ? 'review' : 'pending'} />,
      <div className="flex flex-col gap-1">
        <div className="relative group">
          <button className="text-amber-600 hover:underline text-xs font-medium">Payment Actions</button>
          <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-10 hidden group-hover:block min-w-[120px]">
            {getPaymentActions(row).map(action => (
              <button
                key={action.key}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedRow(row);
                  setSelectedAction(action.key);
                  setIsPaymentModalOpen(true);
                }}
                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 ${action.color}`}
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); handleDuplicateRow(row); }}
          className="text-slate-600 hover:underline text-xs"
        >
          Duplicate
        </button>
      </div>
    ];
  };

  const renderMobileCard = (row) => {
    const { property, space, tenant, tenantDisplayName } = getRowContext(row);
    const calculationHelp = getScheduleCalculationHelp(row);
    const tenantLabel = tenant ? `${tenant.firstName} ${tenant.lastName}` : tenantDisplayName;
    const statusKey = String(row.status || '').toLowerCase();
    const isPaid = statusKey === 'paid' || Boolean(row.paidToDate);
    const isWaived = statusKey === 'waived';
    const isEarlyTerminationWaiver = isWaived && row?.metadata?.earlyTermination?.action === 'waived_future_period';

    return (
      <article key={row.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm overflow-hidden">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900 truncate">{property?.name || row?.metadata?.propertyName || 'Property'}</div>
            <div className="mt-0.5 text-xs text-slate-500 truncate">{space?.name || row?.metadata?.spaceName || 'Whole Property'}</div>
          </div>
          <Badge label={row.status} status={row.status === 'Paid' ? 'active' : row.status === 'Unpaid' ? 'overdue' : row.status === 'Waived' ? 'review' : 'pending'} />
        </div>

        <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 border-y border-slate-100 py-3">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Due Date</div>
            <div className="mt-1 text-sm font-medium text-slate-900">{calculations.formatDateAU(row.dueDate)}</div>
            <div className="text-[11px] text-slate-400">FY {row.financialYear}</div>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Rent Period</div>
            <div className="mt-1 text-xs text-slate-700">{calculations.formatDateAU(row.periodStart)}</div>
            <div className="text-[11px] text-slate-400">to {calculations.formatDateAU(row.periodEnd)}</div>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Tenant</div>
            <div className="mt-1 text-sm text-slate-700 break-words">{tenantLabel === 'Tenant not assigned' ? '—' : tenantLabel}</div>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Gross Rent</div>
            <div className="mt-1 text-sm font-semibold text-slate-900" title={calculationHelp}>${Number(row.grossRentDue || 0).toFixed(2)}</div>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Deductions</div>
            <div className="mt-1 text-sm font-medium text-red-600" title={calculationHelp}>${Number(row.totalDeductions || 0).toFixed(2)}</div>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Net Owner</div>
            <div className="mt-1 text-sm font-semibold text-emerald-600" title={calculationHelp}>${Number(row.netOwnerAmount || 0).toFixed(2)}</div>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between gap-3">
          <div className={`text-xs ${isPaid ? 'font-semibold text-emerald-700' : isWaived ? 'font-semibold text-indigo-700' : 'text-slate-400'}`}>
            {isPaid
              ? row.paidToDate ? `Paid ${calculations.formatDateAU(row.paidToDate)}` : 'Paid'
              : isWaived
                ? isEarlyTerminationWaiver ? 'Waived · lease ended' : 'Waived'
                : 'Not paid'}
          </div>
          <div className="text-[11px] text-slate-400">
            Agent ${Number(row.agentFeeAmount || 0).toFixed(2)} · Other ${Number(row.otherFees || 0).toFixed(2)}
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {(statusKey === 'unpaid' || statusKey === 'partial') && (
            <button
              type="button"
              onClick={() => handleQuickMarkPaid(row)}
              disabled={markingPaidId === row.id}
              className="min-h-[40px] inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-amber-300 bg-white px-3 py-2 text-xs font-semibold text-amber-700 hover:bg-amber-50 disabled:opacity-50"
            >
              {markingPaidId === row.id ? <SafeIcon icon="Loader" className="animate-spin" /> : <SafeIcon icon="DollarSign" />}
              Mark as Paid
            </button>
          )}
          <details className="relative flex-1">
            <summary className="min-h-[40px] list-none cursor-pointer rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-center text-xs font-semibold text-amber-700">
              Payment Actions
            </summary>
            <div className="absolute right-0 top-full z-30 mt-1 min-w-[160px] overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-xl">
              {getPaymentActions(row).map(action => (
                <button
                  key={action.key}
                  type="button"
                  onClick={() => {
                    setSelectedRow(row);
                    setSelectedAction(action.key);
                    setIsPaymentModalOpen(true);
                  }}
                  className={`block w-full px-3 py-2 text-left text-xs font-medium hover:bg-slate-50 ${action.color}`}
                >
                  {action.label}
                </button>
              ))}
            </div>
          </details>
          <button
            type="button"
            onClick={() => handleDuplicateRow(row)}
            className="min-h-[40px] rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600"
          >
            Duplicate
          </button>
        </div>
      </article>
    );
  };

  const fySupportingText = fyFilter === 'All' ? 'All financial years' : `FY ${fyFilter}`;

  return (
    <div className="animate-fade-in min-w-0 overflow-x-hidden">
      <PageHeader
        title="Rent Schedules"
        subtitle="Track expected rent, agent deductions, and final owner disbursements."
        actionLabel="Generate Schedule"
        onAction={() => setIsGenerateModalOpen(true)}
      />
      <details className="mb-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
        <summary className="cursor-pointer select-none font-medium text-slate-700 inline-flex items-center gap-2">
          <SafeIcon icon="Info" className="text-slate-400" />
          How rent schedule amounts are calculated
        </summary>
        <div className="mt-2 space-y-1 text-xs leading-5">
          <p>Rent amount is first annualised from its recorded rent basis. The annual amount is then divided by the payment frequency to produce gross rent per schedule period.</p>
          <p>Agent Fee = Gross Rent × Management Fee %. Total Deductions = Agent Fee + Other Deductions. Net Owner Amount = Gross Rent − Total Deductions.</p>
          <p>Hover the calculated amount cells or the info icon beside Net Owner to see the actual numbers used for rows generated with the new calculation snapshot.</p>
          <p>Early-terminated leases remain visible for audit history. Future waived rows are excluded from collection-rate and upcoming-receipt calculations and cannot be duplicated.</p>
        </div>
      </details>

      <FilterBar
        onSearch={setSearchTerm}
        onClear={() => {
          setSearchTerm('');
          setStatusFilter('All');
          setFyFilter('All');
          setSelectedPropertyId('All');
          setSortConfig({ key: null, direction: 'asc' });
          setSummaryFilter('all');
        }}
      >
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 text-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-500"
        >
          <option value="All">All Statuses</option>
          <option value="Unpaid">Unpaid</option>
          <option value="Partial">Partial</option>
          <option value="Paid">Paid</option>
          <option value="Waived">Waived</option>
          <option value="Disputed">Disputed</option>
        </select>
        <select
          value={fyFilter}
          onChange={(e) => setFyFilter(e.target.value)}
          className="px-4 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 text-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-500"
        >
          {uniqueFinancialYears.map(fy => (
            <option key={fy} value={fy}>{fy === 'All' ? 'All Financial Years' : fy}</option>
          ))}
        </select>
        <div className="inline-flex min-h-[40px] items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800" title="Unpaid or partial rent rows with a due date before today">
          <SafeIcon icon="Calendar" className="text-amber-600" />
          <span>Past due unpaid</span>
          <span className="inline-flex min-w-[22px] items-center justify-center rounded-full bg-amber-200 px-1.5 py-0.5 text-[10px] text-amber-900">{pastDueUnpaidRows.length}</span>
        </div>
        <button
          type="button"
          onClick={handleBulkMarkPaid}
          disabled={pastDueUnpaidRows.length === 0 || Boolean(bulkPaidProgress)}
          className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
          title="Mark all unpaid or partial rent payments due before today as paid"
        >
          <SafeIcon icon="CheckCircle" />
          Mark Past-Due Paid
          <span className="rounded-full bg-white/20 px-1.5 py-0.5 text-[10px]">{pastDueUnpaidRows.length}</span>
        </button>
      </FilterBar>

      {propertyChipData.length > 0 && (
        <section className="mb-5 min-w-0">
          <div className="mb-2 text-xs font-semibold text-slate-700">Filter by Property</div>
          <div
            className="w-full max-w-full overflow-x-auto overscroll-x-contain pb-1"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className="flex min-w-max items-center gap-2 pr-2">
              <button
                type="button"
                onClick={() => setSelectedPropertyId('All')}
                className={`min-h-[40px] rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${
                  selectedPropertyId === 'All'
                    ? 'border-amber-500 bg-amber-500 text-white shadow-sm'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-amber-300 hover:bg-amber-50'
                }`}
              >
                All Properties
              </button>
              {propertyChipData.map(property => (
                <button
                  key={property.id}
                  type="button"
                  onClick={() => setSelectedPropertyId(property.id)}
                  className={`min-h-[40px] rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${
                    selectedPropertyId === property.id
                      ? 'border-amber-500 bg-amber-500 text-white shadow-sm'
                      : 'border-slate-200 bg-white text-slate-700 hover:border-amber-300 hover:bg-amber-50'
                  }`}
                >
                  <span>{property.name}</span>
                  <span className={`ml-2 inline-flex min-w-[22px] items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] ${
                    selectedPropertyId === property.id ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'
                  }`}>
                    {property.count}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm">
        <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm font-semibold text-slate-800">
            Summary for <span className="text-amber-600">{selectedPropertyLabel}</span>
          </div>
          <div className="inline-flex w-fit items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-600">
            <SafeIcon icon="Calendar" className="text-slate-400" />
            {fySupportingText}
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Income</div>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <SummaryMetricCard
                label="Total Rent Collected"
                value={calculations.formatCurrencyAUD(financialSummary.totalRentCollected)}
                supportingText={`${fySupportingText} · paid rows`}
                icon="DollarSign"
                tone="emerald"
              />
              <SummaryMetricCard
                label="Net Income"
                value={calculations.formatCurrencyAUD(financialSummary.netIncome)}
                supportingText="Collected rent less deductions"
                icon="BarChart2"
                tone="blue"
              />
              <SummaryMetricCard
                label="Collection Rate"
                value={`${financialSummary.collectionRate.toFixed(1)}%`}
                supportingText="Paid rent due to date"
                icon="Percent"
                tone="violet"
              />
              <SummaryMetricCard
                label="Upcoming Receipts"
                value={calculations.formatCurrencyAUD(financialSummary.upcomingReceipts)}
                supportingText="Next 30 days"
                icon="Calendar"
                tone="amber"
              />
            </div>
          </div>
          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Expenses &amp; deductions</div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <SummaryMetricCard
                label="Total Agent Fees"
                value={calculations.formatCurrencyAUD(financialSummary.totalAgentFees)}
                supportingText="Paid rent rows"
                icon="Percent"
                tone="red"
                active={summaryFilter === 'agentFee'}
                onClick={() => setSummaryFilter(current => current === 'agentFee' ? 'all' : 'agentFee')}
              />
              <SummaryMetricCard
                label="Other Deductions"
                value={calculations.formatCurrencyAUD(financialSummary.totalOtherDeductions)}
                supportingText="Paid rent rows"
                icon="MinusCircle"
                tone="red"
                active={summaryFilter === 'otherDeductions'}
                onClick={() => setSummaryFilter(current => current === 'otherDeductions' ? 'all' : 'otherDeductions')}
              />
              <SummaryMetricCard
                label="Total Deductions"
                value={calculations.formatCurrencyAUD(financialSummary.totalDeductions)}
                supportingText="Agent fees + other deductions"
                icon="TrendingDown"
                tone="red"
                active={summaryFilter === 'totalDeductions'}
                onClick={() => setSummaryFilter(current => current === 'totalDeductions' ? 'all' : 'totalDeductions')}
              />
            </div>
          </div>
        </div>
        {summaryFilter !== 'all' && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <span>Showing rows with {summaryFilter === 'agentFee' ? 'agent fees' : summaryFilter === 'otherDeductions' ? 'other deductions' : 'total deductions'}.</span>
            <button type="button" onClick={() => setSummaryFilter('all')} className="font-semibold underline hover:no-underline">Clear deduction filter</button>
          </div>
        )}
      </section>

      {filteredSchedules.length > 0 ? (
        <>
          <div className="mb-3 flex items-center gap-2 lg:hidden">
            <span className="text-xs font-semibold text-slate-500">Sort</span>
            <select
              value={sortConfig.key || ''}
              onChange={(e) => {
                const key = e.target.value || null;
                setSortConfig({ key, direction: key === 'dueDate' || key === 'periodStart' || key === 'paidStatus' ? 'desc' : 'asc' });
              }}
              className="min-h-[40px] flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-500"
              aria-label="Sort rent schedules"
            >
              <option value="">Default order</option>
              <option value="dueDate">Due date</option>
              <option value="property">Property</option>
              <option value="tenant">Tenant</option>
              <option value="grossRent">Gross rent</option>
              <option value="netOwner">Net owner</option>
              <option value="paidStatus">Paid status</option>
              <option value="status">Status</option>
            </select>
            <button
              type="button"
              onClick={() => sortConfig.key && setSortConfig(current => ({ ...current, direction: current.direction === 'asc' ? 'desc' : 'asc' }))}
              disabled={!sortConfig.key}
              className="min-h-[40px] rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 disabled:opacity-40"
              aria-label="Reverse sort direction"
            >
              {sortConfig.direction === 'asc' ? 'Asc' : 'Desc'}
            </button>
          </div>
          <div className="hidden lg:block">
            <DataTable
              columns={columns}
              data={sortedSchedules}
              renderRow={renderRow}
              emptyMessage="No rent schedule rows found matching your criteria."
            />
          </div>
          <div className="space-y-3 lg:hidden">
            {sortedSchedules.map(renderMobileCard)}
          </div>
        </>
      ) : (
        <EmptyState
          title="No Rent Schedules Generated"
          description="Generate rent schedules from your active leases to track expected income, deductions, and owner disbursements."
          actionLabel="Generate Schedule"
          onAction={() => setIsGenerateModalOpen(true)}
          icon="Calendar"
        />
      )}

      <Modal
        isOpen={isGenerateModalOpen}
        onClose={() => !isGenerating && setIsGenerateModalOpen(false)}
        title="Generate Rent Schedule"
        maxWidth="max-w-4xl"
      >
        <div className="relative">
          <RentScheduleGenerateForm
            onSubmit={handleGenerateSchedule}
            onCancel={() => !isGenerating && setIsGenerateModalOpen(false)}
            isSubmitting={isGenerating}
          />
          {isGenerating && generationProgress && <GenerationProgressPanel progress={generationProgress} />}
        </div>
      </Modal>

      <Modal
        isOpen={isPaymentModalOpen}
        onClose={() => { setIsPaymentModalOpen(false); setSelectedRow(null); setSelectedAction(null); }}
        title={selectedAction ? `Mark as ${selectedAction.charAt(0).toUpperCase() + selectedAction.slice(1)}` : 'Payment Action'}
      >
        <PaymentActionModal
          action={selectedAction}
          scheduleRow={selectedRow}
          onSubmit={handlePaymentAction}
          onCancel={() => { setIsPaymentModalOpen(false); setSelectedRow(null); setSelectedAction(null); }}
        />
      </Modal>

      <Modal
        isOpen={isRegenerateModalOpen}
        onClose={() => { setIsRegenerateModalOpen(false); setSelectedRow(null); }}
        title="Regenerate Rent Schedule"
      >
        <RegenerateConfirmModal
          lease={selectedRow?.lease}
          newRent={selectedRow?.newRent}
          effectiveDate={selectedRow?.effectiveDate}
          onConfirm={confirmRegenerate}
          onCancel={() => { setIsRegenerateModalOpen(false); setSelectedRow(null); }}
        />
      </Modal>

      {paidConfirmation && (
        <div
          role="status"
          aria-live="polite"
          className="fixed top-20 right-4 z-[80] pointer-events-none flex items-center gap-2 rounded-xl border border-emerald-200 bg-white px-3 py-2 shadow-lg"
        >
          <SafeIcon icon="CheckCircle" className="text-emerald-500 text-base" />
          <span className="text-sm font-medium text-emerald-700">{paidConfirmation.message}</span>
        </div>
      )}
      {bulkPaidProgress && <BulkPaidProgressPanel progress={bulkPaidProgress} />}

    </div>
  );
};

export default RentSchedulesPage;